// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import ScheduleIcon from '@mui/icons-material/Schedule'
import Tab from '@mui/material/Tab'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tabs from '@mui/material/Tabs'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import get from 'lodash/get'
import isEqual from 'lodash/isEqual'
import React from 'react'
import { Mutation } from 'react-apollo'
import { Trans } from 'react-i18next'
import { getTotalWeight, getTotalWeightColor } from '../../../common/weight/weightHelpers'
import { i18n } from '../../index'
import {
  upsertVitrineProductTimeSlotWeight as upsertVitrineProductTimeSlotWeightMutation,
  vitrineProductWeightsQuery,
} from './vitrineProductQueries'
import css from './vitrineProductTimeSlotWeightsTable.css'
import type { VitrineProductTimeslotWeight } from './vitrineProductTypes'

type Data = {
  Salty: Array<VitrineProductTimeslotWeight>
  Sweet: Array<VitrineProductTimeslotWeight>
}
type Props = {
  stationId: string
  timeSlot: string
  data: Data
  weekday: string
  onWeekdayChange: (...args: Array<any>) => any
}
type State = {
  data: Data
}

const updateCache = (cache: any, response: any, queryVariables: any) => {
  const data = cache.readQuery({
    query: vitrineProductWeightsQuery,
    variables: queryVariables,
  })

  const updateCacheItem = (item) => {
    if (
      item.productType === response.productType &&
      Number(item.timeslot) === Number(response.timeslot) &&
      item.weekday === response.weekday
    ) {
      return response
    }

    return item
  }

  const updatedData = {
    ...data,
    saltyVitrineProductTimeSlotWeights: data.saltyVitrineProductTimeSlotWeights.map(updateCacheItem),
    sweetVitrineProductTimeSlotWeights: data.sweetVitrineProductTimeSlotWeights.map(updateCacheItem),
  }
  cache.writeQuery({
    query: vitrineProductWeightsQuery,
    data: updatedData,
    variables: queryVariables,
  })
}

class VitrineProductTimeSlotWeightsTable extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      data: props.data,
    }
  }

  componentDidUpdate(prevProps: any, prevState: any) {
    const { data } = this.props

    if (data && !isEqual(data, prevProps.data)) {
      this.setState({
        data: data,
      })
    }
  }

  onWeightChange = (item: VitrineProductTimeslotWeight, newValue: number) => {
    const { data } = this.state
    this.setState({
      data: {
        Salty: get(data, 'Salty', []).map((saltyItem) => {
          if (item.productType === 'Salty' && saltyItem.timeslot === item.timeslot) {
            return { ...saltyItem, weight: newValue }
          }

          return saltyItem
        }),
        Sweet: get(data, 'Sweet', []).map((saltyItem) => {
          if (item.productType === 'Sweet' && saltyItem.timeslot === item.timeslot) {
            return { ...saltyItem, weight: newValue }
          }

          return saltyItem
        }),
      },
    })
  }
  onSubmit = (onMutate: (...args: Array<any>) => any, item: VitrineProductTimeslotWeight) => {
    const { stationId, weekday } = this.props
    onMutate({
      variables: {
        stationId,
        vitrineProductTimeSlotWeight: {
          timeslot: Number(item.timeslot),
          weight: Number(item.weight),
          productType: item.productType,
          weekday,
        },
      },
      update: (cache: any, { data }: any) =>
        updateCache(cache, data.upsertVitrineProductTimeslotWeight, {
          stationId,
          weekday,
        }),
    })
  }

  render() {
    const { weekday, onWeekdayChange } = this.props
    const { data } = this.state
    const saltyTotalWeight = getTotalWeight(get(data, 'Salty', []))
    const sweetTotalWeight = getTotalWeight(get(data, 'Sweet', []))
    return (
      <Mutation mutation={upsertVitrineProductTimeSlotWeightMutation}>
        {(onMutate) => (
          <div className={css.component}>
            <Tabs
              indicatorColor='primary'
              textColor='primary'
              value={weekday}
              onChange={(e, value) => onWeekdayChange(value)}
            >
              <Tab
                className={css.productTypeTab}
                disableRipple
                value='Weekday'
                label={i18n.t('admin.vitrineProducts.label.Weekday')}
              />
              <Tab
                className={css.productTypeTab}
                disableRipple
                value='Friday'
                label={i18n.t('admin.vitrineProducts.label.Friday')}
              />
              <Tab
                className={css.productTypeTab}
                disableRipple
                value='Weekend'
                label={i18n.t('admin.vitrineProducts.label.Weekend')}
              />
            </Tabs>

            <div className={css.timeSlotTableContainer}>
              <Typography variant='subtitle2' className={css.subtitle}>
                <Trans
                  i18nKey='admin.vitrineProducts.weights.saltyTimeSlotWeightsTitle'
                  values={{ totalWeight: saltyTotalWeight }}
                  components={{ totalWeight: <strong style={{ color: getTotalWeightColor(saltyTotalWeight) }} /> }}
                />
              </Typography>
              <div className={css.timeSlotTable}>
                <Table>
                  <TableHead>
                    <TableRow>
                      {get(data, 'Salty', []).map((item, i) => {
                        return (
                          <TableCell key={i} className={css.tableHeading}>
                            <div className={css.hourLabel}>
                              <ScheduleIcon />
                              <span>{item.timeslot}</span>
                            </div>
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      {get(data, 'Salty', []).map((item, i) => {
                        return (
                          <TableCell key={i} className={css.tableCell}>
                            <TextField type='number' value={item.weight || ''} disabled={true} />
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </div>

            <div className={css.timeSlotTableContainer}>
              <Typography variant='subtitle2' className={css.subtitle}>
                <Trans
                  i18nKey='admin.vitrineProducts.weights.sweetTimeSlotWeightsTitle'
                  values={{ totalWeight: sweetTotalWeight }}
                  components={{ totalWeight: <strong style={{ color: getTotalWeightColor(sweetTotalWeight) }} /> }}
                />
              </Typography>
              <div className={css.timeSlotTable}>
                <Table>
                  <TableHead>
                    <TableRow>
                      {get(data, 'Sweet', []).map((item, i) => {
                        return (
                          <TableCell key={i} className={css.tableHeading}>
                            <div className={css.hourLabel}>
                              <ScheduleIcon />
                              <span>{item.timeslot}</span>
                            </div>
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      {get(data, 'Sweet', []).map((item, i) => {
                        return (
                          <TableCell key={i} className={css.tableCell}>
                            <TextField type='number' value={item.weight || ''} disabled={true} />
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        )}
      </Mutation>
    )
  }
}

export default VitrineProductTimeSlotWeightsTable
